<script>
import { useVuelidate } from '@vuelidate/core';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { required, minLength, email } from '@vuelidate/validators';
import FormInput from '../../../components/Form/Input.vue';
import { resetPassword } from 'v3/api/auth';
import SubmitButton from '../../../components/Button/SubmitButton.vue';
import Slogan from '../../SloganBox.vue';
import GapifyHeader from '../../AuthHeader.vue';
import GapifySignBox from '../../AuthFormBox.vue';
import AuthLayout from '../../AuthLayout.vue';
import { BaseButton } from '../../../components/Button';
import OnboardingMotionLogin from '../../login/OnboardingMotionLogin.vue';

export default {
  components: {
    OnboardingMotionLogin,
    GapifySignBox,
    GapifyHeader,
    Slogan,
    FormInput,
    SubmitButton,
    AuthLayout,
    BaseButton,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      credentials: { email: '' },
      resetPassword: {
        message: '',
        showLoading: false,
      },
      error: '',
    };
  },
  computed: {
    ...mapGetters({ globalConfig: 'globalConfig/get' }),
  },
  validations() {
    return {
      credentials: {
        email: {
          required,
          email,
          minLength: minLength(4),
        },
      },
    };
  },
  methods: {
    showAlertMessage(message) {
      // Reset loading, current selected agent
      this.resetPassword.showLoading = false;
      useAlert(message);
    },
    submit() {
      this.resetPassword.showLoading = true;
      resetPassword(this.credentials)
        .then(res => {
          let successMessage = this.$t('RESET_PASSWORD.API.SUCCESS_MESSAGE');
          if (res.data && res.data.message) {
            successMessage = res.data.message;
          }
          this.showAlertMessage(successMessage);
        })
        .catch(error => {
          let errorMessage = this.$t('RESET_PASSWORD.API.ERROR_MESSAGE');
          if (error?.response?.data?.message) {
            errorMessage = error.response.data.message;
          }
          this.showAlertMessage(errorMessage);
        });
    },
  },
};
</script>

<template>
  <div
    v-if="false"
    class="flex w-full min-h-screen bg-woot-25 dark:bg-slate-900"
  >
    <div class="w-full md:w-1/2 px-4 flex justify-center items-center">
      <GapifySignBox>
        <template #content>
          <section class="max-w-5xl mx-auto">
            <GapifyHeader
              :title-text="$t('RESET_PASSWORD.TITLE')"
              :description="$t('RESET_PASSWORD.DESCRIPTION')"
              :show-description="true"
              description-class="mt-5 mb-4 text-sm font-normal leading-6 tracking-normal text-descriptionText dark:text-woot-50"
            />
            <form @submit.prevent="submit">
              <FormInput
                v-model="credentials.email"
                name="email_address"
                :has-error="v$.credentials.email.$error"
                :label="$t('RESET_PASSWORD.EMAIL.LABEL')"
                :error-message="$t('RESET_PASSWORD.EMAIL.ERROR')"
                :placeholder="$t('RESET_PASSWORD.EMAIL.PLACEHOLDER')"
                :is-first-item="true"
                @input="v$.credentials.email.$touch"
              />
              <div class="mt-10 sm:mt-12">
                <SubmitButton
                  :disabled="
                    v$.credentials.email.$invalid || resetPassword.showLoading
                  "
                  :button-text="$t('RESET_PASSWORD.SUBMIT')"
                  :loading="resetPassword.showLoading"
                />
                <p
                  class="mt-5 -mb-1 text-sm text-descriptionText dark:text-woot-50 items-center text-center"
                >
                  <router-link to="/auth/login">
                    {{ $t('RESET_PASSWORD.GO_BACK_TO_LOGIN') }}
                  </router-link>
                </p>
              </div>
            </form>
          </section>
        </template>
      </GapifySignBox>
    </div>

    <Slogan class="hidden md:block w-1/2">
      <template #slogan-image>
        <slogan-image />
      </template>
    </Slogan>
  </div>
  <AuthLayout>
    <template #right>
      <OnboardingMotionLogin />
    </template>
    <template #left>
      <div
        class="flex flex-col w-full h-full min-[1440px]:justify-start justify-between px-4 min-[1440px]:px-10 min-[960px]:px-8 min-[1440px]:py-10 min-[960px]:pt-8 dark:bg-slate-900"
      >
        <div class="flex flex-col gap-6 w-full max-w-full">
          <section class="flex flex-col w-full max-w-full">
            <GapifyHeader
              :title-text="$t('RESET_PASSWORD.TITLE')"
              :description="$t('RESET_PASSWORD.DESCRIPTION')"
              :show-description="false"
              description-class="mt-5 !text-3 text-center !text-descriptionText dark:text-slate-400"
            />
            <div
              class="flex flex-col min-[960px]:flex-row font-medium text-[#787882] dark:text-slate-300 text-[14px] items-center mt-2 bp960:mt-1 bp1440:mt-1 text-center"
            >
              {{ $t('RESET_PASSWORD.DESCRIPTION') }}
            </div>
            <form @submit.prevent="submit" class="mt-6">
              <FormInput
                v-model="credentials.email"
                name="email_address"
                :has-error="v$.credentials.email.$error"
                :label="$t('RESET_PASSWORD.EMAIL.LABEL')"
                :error-message="$t('RESET_PASSWORD.EMAIL.ERROR')"
                :placeholder="$t('RESET_PASSWORD.EMAIL.PLACEHOLDER')"
                :is-first-item="true"
                @input="v$.credentials.email.$touch"
                required
              />
            </form>
          </section>
        </div>

        <div
          class="flex flex-col min-[960px]:flex-row items-stretch min-[960px]:items-center py-6 min-[1440px]:pt-12 gap-4 min-[960px]:gap-0"
        >
          <div
            class="flex flex-col min-[960px]:flex-row min-[1440px]:w-full min-[1440px]:justify-end items-center gap-2 w-full"
          >
            <BaseButton
              status="Default"
              type="Text"
              :text="$t('RESET_PASSWORD.GO_BACK_TO_LOGIN')"
              class="w-full min-[1440px]:w-auto !px-3 order-2 min-[960px]:order-1"
              @click="$router.push({ path: '/auth/login' })"
            />
            <BaseButton
              status="gapify"
              type="Fill"
              size="Default"
              :text="$t('RESET_PASSWORD.SUBMIT')"
              :loading="resetPassword.showLoading"
              :disabled="
                v$.credentials.email.$invalid || resetPassword.showLoading
              "
              class="w-full min-[1440px]:w-auto order-1 min-[960px]:order-2"
              @click="submit"
            />
          </div>
        </div>
      </div>
    </template>
  </AuthLayout>
</template>
